import { computed, inject, provide, toValue, type ComputedRef, type MaybeRefOrGetter } from "vue";

/**
 * Where portalled panels render, such as PixSelect's list of options.
 *
 * Panels are teleported out of their component so no `overflow` or stacking context clips them;
 * by default they go to `body`. That breaks inside a native modal dialog
 * (`<dialog>` opened with `showModal()`): the browser puts the dialog into the top layer and makes
 * everything outside it inert, so a panel in `body` renders below the dialog and takes no clicks.
 * The same holds for any container that makes the rest of the page inert.
 *
 * Such a container provides itself as the target (`providePortalTarget`), and every component
 * below it renders its panels there. Without a provider, or while the provided element is not
 * mounted, nothing changes: panels go to `body`.
 *
 * Key after the library's provide/inject convention (PATTERN_LIBRARY_GUIDELINES.md, "Patterns").
 */
export const PIX_PORTAL_TARGET = "pix-portal-target";

type PortalTarget = MaybeRefOrGetter<HTMLElement | null | undefined>;

/** Offers `target` to the components below as the place for their panels. */
export function providePortalTarget(target: PortalTarget): void {
  provide(PIX_PORTAL_TARGET, target);
}

/** The element an ancestor provided, or `body` without one or while it is not mounted. */
export function usePortalTarget(): ComputedRef<HTMLElement | "body"> {
  const target = inject<PortalTarget | undefined>(PIX_PORTAL_TARGET, undefined);
  return computed(() => toValue(target) ?? "body");
}
